import { useEffect, useMemo, useState } from 'react'
import {
  Arrow,
  Circle,
  Image as KonvaImage,
  Layer,
  Line,
  Path,
  Stage,
  Text,
} from 'react-konva'

import {
  getAnnotationSizeNumber,
  type AnnotationToolPreset,
  type KonvaAnnotationGeometry,
} from './annotationSchema'
import { createKonvaAnnotationFromPreset } from './annotationPresets'
import type { AnnotationSize } from '@/services/hooks/template_attributes'
import KonvaAnnotationBoundsShape from './KonvaAnnotationBoundsShape'

type KonvaAnnotationPreviewProps = {
  preset: AnnotationToolPreset
  size?: AnnotationSize
  strokeColor: string
}

type KonvaPreviewImageProps = {
  boxSize: number
  src: string
  x: number
  y: number
}

const getPreviewSize = (size?: AnnotationSize) =>
  typeof size === 'number' ? size * 4 : getAnnotationSizeNumber(size)

const getGeometryPreviewBounds = (
  geometry: KonvaAnnotationGeometry,
  size?: AnnotationSize,
) => {
  if (geometry.mode === 'bounds') {
    return {
      height: geometry.height,
      width: geometry.width,
    }
  }

  const previewSize = getPreviewSize(size)

  return {
    height: previewSize,
    width: previewSize,
  }
}

const KonvaPreviewImage = ({ boxSize, src, x, y }: KonvaPreviewImageProps) => {
  const [image, setImage] = useState<HTMLImageElement | null>(null)

  useEffect(() => {
    const nextImage = new window.Image()
    nextImage.onload = () => setImage(nextImage)
    nextImage.src = src

    return () => {
      nextImage.onload = null
    }
  }, [src])

  if (!image) return null

  const imageHeight =
    image.naturalWidth > 0
      ? boxSize * (image.naturalHeight / image.naturalWidth)
      : boxSize

  return (
    <KonvaImage
      image={image}
      x={x}
      y={y + (boxSize - imageHeight) / 2}
      width={boxSize}
      height={imageHeight}
    />
  )
}

export const KonvaAnnotationPreview = ({
  preset,
  size,
  strokeColor,
}: KonvaAnnotationPreviewProps) => {
  const annotation = useMemo(
    () =>
      createKonvaAnnotationFromPreset({
        preset: {
          ...preset,
          defaultStyle: {
            ...preset.defaultStyle,
            stroke: strokeColor,
          },
        },
        x: 0,
        y: 0,
      }),
    [preset, strokeColor],
  )
  const { width: stageWidth, height: stageHeight } = getGeometryPreviewBounds(
    annotation.geometry,
    size,
  )
  const strokeWidth = annotation.style.strokeWidth ?? 2
  const stagePadding = strokeWidth / 2
  const paddedStageWidth = stageWidth + stagePadding * 2
  const paddedStageHeight = stageHeight + stagePadding * 2
  const stroke = annotation.style.stroke ?? strokeColor

  return (
    <Stage
      width={paddedStageWidth}
      height={paddedStageHeight}
      className="opacity-60"
    >
      <Layer listening={false}>
        {(annotation.kind === 'rect' ||
          annotation.kind === 'roundRect' ||
          annotation.kind === 'circle' ||
          annotation.kind === 'ellipse' ||
          annotation.kind === 'triangle' ||
          annotation.kind === 'diamond') && (
          <KonvaAnnotationBoundsShape
            kind={annotation.kind}
            width={stageWidth}
            height={stageHeight}
            offsetX={stagePadding}
            offsetY={stagePadding}
            roundRectCornerRadius={
              annotation.geometry.mode === 'bounds'
                ? annotation.geometry.cornerRadius
                : undefined
            }
            isLegacySquare={annotation.legacyType === 'square'}
            isLegacyTriangle
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
        {annotation.kind === 'line' && (
          <Line
            points={[
              stagePadding,
              stagePadding + stageHeight,
              stagePadding + stageWidth,
              stagePadding,
            ]}
            stroke={stroke}
            strokeWidth={strokeWidth}
          />
        )}
        {annotation.kind === 'arrow' && (
          <Arrow
            points={[
              stagePadding,
              stagePadding + stageHeight,
              stagePadding + stageWidth,
              stagePadding,
            ]}
            stroke={stroke}
            fill={stroke}
            strokeWidth={strokeWidth}
            pointerLength={Math.max(stageWidth * 0.18, 7)}
            pointerWidth={Math.max(stageWidth * 0.18, 7)}
          />
        )}
        {annotation.kind === 'bubble' && (
          <>
            <Circle
              x={stagePadding + stageWidth * 0.28}
              y={stagePadding + stageHeight * 0.72}
              radius={Math.max(Math.min(stageWidth, stageHeight) * 0.16, 5)}
              stroke={stroke}
              strokeWidth={strokeWidth}
            />
            <Arrow
              points={[
                stagePadding + stageWidth * 0.4,
                stagePadding + stageHeight * 0.6,
                stagePadding + stageWidth,
                stagePadding,
              ]}
              stroke={stroke}
              fill={stroke}
              strokeWidth={strokeWidth}
              pointerLength={Math.max(stageWidth * 0.16, 7)}
              pointerWidth={Math.max(stageWidth * 0.16, 7)}
            />
            {annotation.bubbleValue && (
              <Text
                x={stagePadding + stageWidth * 0.12}
                y={stagePadding + stageHeight * 0.62}
                width={stageWidth * 0.32}
                text={annotation.bubbleValue}
                fill="#000000"
                fontSize={Math.max(stageWidth * 0.22, 10)}
                align="center"
              />
            )}
          </>
        )}
        {annotation.kind === 'icon' &&
          (annotation.iconName === 'checkmark' ? (
            <Path
              data="M19.7577 0.890952C19.2531 0.396074 18.4455 0.395201 17.9398 0.888987L6.05631 12.4927C6.04525 12.5035 6.02782 12.5042 6.01592 12.4943L2.68278 9.72755C2.14071 9.27759 1.33871 9.34201 0.875407 9.87272C0.395504 10.4225 0.462772 11.2596 1.0243 11.7257L5.25657 15.2383C5.78479 15.672 6.55469 15.6388 7.04368 15.1614L19.7557 2.74868C20.2771 2.23953 20.278 1.40123 19.7577 0.890952Z"
              x={stagePadding}
              y={stagePadding + (stageHeight - stageWidth * (16 / 21)) / 2}
              scaleX={stageWidth / 21}
              scaleY={(stageWidth * (16 / 21)) / 16}
              fill={stroke}
            />
          ) : annotation.iconSrc ? (
            <KonvaPreviewImage
              boxSize={stageWidth}
              x={stagePadding}
              y={stagePadding}
              src={annotation.iconSrc}
            />
          ) : (
            <Text
              x={stagePadding}
              y={stagePadding + stageHeight / 2 - 5}
              width={stageWidth}
              text={annotation.iconName ?? annotation.legacyType ?? 'icon'}
              fill={stroke}
              fontSize={10}
              align="center"
              ellipsis
            />
          ))}
      </Layer>
    </Stage>
  )
}
